import { Fragment, useState, useCallback } from 'react'
import AccordionCards from '@/components/section/accordion-cards'
import BarGraph from '@/components/bar-graph'

import ComparisonTable from '@/components/comparison-table'
import EventsSchedule from '@/components/events-schedule/event-schedule'

import Lottie from '@/components/lottie'
import Image from '@/components/ui/image'
import Quote from '@/components/ui/quote'
import RichText from '@/components/ui/rich-text'
import { SetOfMasonaryCards } from '@/components/section/set-of-masonary-cards.tsx'
import SectionCta from '@/components/section/cta'
import SectionFaqs from '@/components/section/faqs'
import SectionForm from '@/components/section/form'
import SectionPrebuilt from '@/components/section/prebuilt'
import SectionTabSection from '@/components/section/tab-section'
import SetOfCards from '@/components/section/set-of-cards'
import SetOfImages from '@/components/section/set-of-images'
import SetOfPricing from '@/components/section/set-of-pricing'
import Slider from '@/components/section/slider'
import Table from '@/components/table'
import SearchFilterBar from '@/components/search-filter-bar'
import { getPdfPath, isPrint } from '@/utils/print'
import { cx } from '@/utils/strings'
import colors from '@/constants/colors'

import { Experience } from '@ninetailed/experience.js-react'
import { mapNtExperiences } from '@/utils/ninetailed'
import ShieldLoader from '@/components/ui/shield-loader'
import PageBackground from '@/components/ui/page-background'
import { Asset } from 'contentful'
import { getLocale, localizedPath } from '@/utils/locale.ts'

export type LandingPageSectionsProps = {
  jobs?: Array<any>
  showSearchFilter?: boolean
  backgroundColor?: string
  sectionsBackgroundImage?: Asset<'WITHOUT_UNRESOLVABLE_LINKS'>
  sections: Array<any>
}

export const SectionLandingPageSectionsComponent = ({
  sections,
  showSearchFilter,
  backgroundColor,
  sectionsBackgroundImage,
  jobs,
}: LandingPageSectionsProps) => {
  const isPrintView = isPrint()
  const [cardFilterSearch, setCardFilterSearch] = useState<string>('')
  const [cardFilterTags, setCardFilterTags] = useState<string[]>([])

  const { indigoBlue, primaryBlue } = colors
  let sectionNo = 0 // Initialize section number
  return (
    <>
      {sectionsBackgroundImage && <PageBackground pageBackgroundImage={sectionsBackgroundImage} />}
      <div className="z-2 relative [&>section:not(.cards-hero,#sticky-cta)]:pt-10 [&>section:not(#sticky-cta)]:pb-10 [&>#sticky-cta]:my-10">
        {sections
          ?.filter((section) => section?.sys?.contentType?.sys && section?.fields)
          ?.map((section: any, index: number, filteredSections) => {
            const {
              sys: { contentType },
              fields,
            } = section

            switch (contentType.sys.id) {
              case 'sectionSetOfCards':
                const isHero = index === 0 && fields?.cards?.length === 1
                sectionNo = !fields?.heading && !isHero ? index - 1 : index // Set section number based on whether there is a heading
                return isHero && isPrintView ? null : (
                  <Fragment key={section.sys.id}>
                    <SetOfCards
                      id={section.sys.id}
                      {...fields}
                      isHero={(index === 0 && fields?.cards?.length === 1) || false}
                      pageOrderNo={sectionNo}
                      isPrintView={isPrintView}
                      filter={
                        showSearchFilter &&
                        !isHero && {
                          search: cardFilterSearch,
                          tags: cardFilterTags,
                        }
                      }
                    />
                    {showSearchFilter && index === 0 && (
                      <SearchFilterBar
                        sections={sections}
                        onSearchChange={(search) => setCardFilterSearch(search)}
                        onFilterChange={(filters) => setCardFilterTags(filters)}
                      />
                    )}
                  </Fragment>
                )
              case 'sectionMasonary':
                return !isPrintView && <SetOfMasonaryCards key={section.sys.id} {...fields} />
              case 'sectionFaqs':
                return <SectionFaqs key={section.sys.id} {...fields} isPrintView={isPrintView} />
              case 'sectionEventSchedule':
                return !isPrintView && <EventsSchedule key={section.sys.id} {...fields} id={section.sys.id} />
              case 'sectionSetOfPricing':
                return (
                  !isPrintView && (
                    <div id="pricing" key={section.sys.id}>
                      <SetOfPricing {...fields} id={section.sys.id} />
                    </div>
                  )
                )
              case 'sectionSetOfImages':
                return (
                  !isPrintView && (
                    <SetOfImages
                      {...fields}
                      pageOrderNo={sectionNo}
                      id={section.sys.id}
                      key={section.sys.id}
                      className={cx(
                        // Remove margin between sections if the next section is a SetOfImages without a heading
                        index !== filteredSections.length - 1 &&
                          filteredSections[index + 1]?.sys.contentType.sys.id === 'sectionSetOfImages' &&
                          !filteredSections[index + 1]?.fields.heading &&
                          '!mb-0',
                        index > 0 &&
                          !section.fields.heading &&
                          filteredSections[index - 1]?.sys.contentType.sys.id === 'sectionSetOfImages' &&
                          '!mt-0'
                      )}
                    />
                  )
                )
              case 'componentComparisonTable':
                return <ComparisonTable key={section.sys.id} {...fields} isPrintView={isPrintView} />
              case 'componentTable':
                return <Table key={section.sys.id} {...fields} />
              case 'componentQuote':
                return (
                  <section key={section.sys.id} className="max-w-8xl m-auto px-5 lg:px-9 my-10 lg:mb-20">
                    <div className="max-w-3xl mx-auto">
                      <Quote {...fields} />
                    </div>
                  </section>
                )
              case 'sectionCta':
                return !isPrintView && <SectionCta {...fields} key={section.sys.id} client:load />
              case 'sectionPrebuilt':
                if (fields.name === 'Jobs list') {
                  fields.properties = jobs
                }
                return !isPrintView && <SectionPrebuilt key={section.sys.id} {...fields} id={section.sys.id} />
              case 'sectionTabSection':
                return (
                  !isPrintView && (
                    <SectionTabSection
                      key={section.sys.id}
                      {...fields}
                      contentfulId={section.sys.id}
                      isHero={index === 0}
                      canExperiment
                    />
                  )
                )
              case 'sectionForm':
                return !isPrintView && <SectionForm key={section.sys.id} {...fields} />
              case 'sectionSlider':
                return (
                  <Slider
                    key={section.sys.id}
                    {...fields}
                    pdfPath={
                      fields.bwPresentation
                        ? getPdfPath(localizedPath(`/resources/presentations/${fields.slug}/`, getLocale()))
                        : ''
                    }
                    className={cx(
                      'mx-auto text-indigoBlue mb-12 px-5 lg:px-10',
                      fields.sliderStyle === 'Presentation' ? 'max-w-8xl' : 'max-w-6xl'
                    )}
                  />
                )
              case 'sectionBarGraph':
                return (
                  !isPrintView && (
                    <section key={section.sys.id} className="max-w-8xl mx-auto px-5 lg:px-9 py-10">
                      <BarGraph {...fields} />
                    </section>
                  )
                )
              case 'componentLottie':
                return (
                  !isPrintView && (
                    <section key={section.sys.id} className="max-w-8xl mx-auto px-5 lg:px-9 py-10">
                      <Lottie {...fields} />
                    </section>
                  )
                )
              case 'componentRichText':
                return !isPrintView ? (
                  <section
                    key={section.sys.id}
                    className={cx(
                      'article max-w-4xl mx-auto px-5 py-10',
                      (backgroundColor === indigoBlue || backgroundColor === primaryBlue) && 'text-white'
                    )}
                  >
                    <RichText {...fields} />
                  </section>
                ) : (
                  <RichText key={section.sys.id} {...fields} />
                )
              case 'landingRichText':
                const { contentWidth, textAlign, tableColor } = fields
                const richText = { body: fields.text?.fields?.body, tableColor, contentWidth, textAlign }
                return !isPrintView ? (
                  <section
                    key={section.sys.id}
                    className={cx(
                      'article',
                      tableColor === 'gray' && `bg-white dark:bg-secondary-800`,
                      tableColor === 'white' && `bg-secondary-50 dark:bg-secondary-800`,
                      contentWidth !== 'wide' && `max-w-3xl mx-auto py-5 lg:py-20 px-5 lg:px-9`
                    )}
                  >
                    <div
                      className={cx(
                        'article [.table-container]:py-10 [.table-container:last-of-type]:mb-0',
                        contentWidth === 'wide' && `mx-auto max-w-8xl px-5 lg:px-9`,
                        textAlign === 'center' && `text-center`,
                        textAlign === 'right' && `text-right`
                      )}
                    >
                      <RichText key={section.sys.id} {...richText} />
                    </div>
                  </section>
                ) : (
                  <RichText key={section.sys.id} {...richText} />
                )
              case 'resourceImage':
                return (
                  !isPrintView && (
                    <section key={section.sys.id} className="max-w-8xl m-auto px-5 lg:px-9 py-10">
                      <Image {...fields} />
                    </section>
                  )
                )
              case 'sectionAccordionCards':
                return (
                  !isPrintView && (
                    <section key={section.sys.id} className="py-10">
                      <AccordionCards {...fields} />
                    </section>
                  )
                )
              default:
                return null
            }
          })}
      </div>
    </>
  )
}

export default function SectionLandingPageSections(props: any): JSX.Element {
  const { sections, searchFilter, backgroundColor, jobs, experiences, id, sectionsBackgroundImage } = props

  const ShieldBeatLoader = useCallback(() => <ShieldLoader containerHeight="800" shieldSize="Large" />, [])

  return (
    <Experience
      {...props}
      id={id}
      experiences={mapNtExperiences(experiences)}
      component={SectionLandingPageSectionsComponent}
      passthroughProps={{ sections, searchFilter, backgroundColor, jobs, sectionsBackgroundImage }}
      loadingComponent={ShieldBeatLoader}
    />
  )
}
